import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import { DemeterDataFrequency, DemeterPageType, DemeterRegion, DemeterTableDefinitionType } from '../../../Generated/Raven-Demeter/api';
import useCommentaryApi from '../../Apis/Hooks/useCommentaryApiHook';
import AggregateRegionSelector from '../../Components/AggregateRegionSelector/AggregateRegionSelector';
import ProjectionChart from '../../Components/Charts/Projection/ProjectionChart';
import SeasonalChart from '../../Components/Charts/Seasonal/SeasonalChart';
import UnitOfMeasureMetricImperialDropdown from '../../Components/Form/Inputs/UnitOfMeasureMetricImperialDropdown';
import TutorialLink from '../../Components/Form/Links/TutorialLink/TutorialLink';
import PageHeaderWithSelections from '../../Components/Headers/PageHeaderWithSelections';
import useTableDefinitionNavigation from '../../Components/Navigation/Hooks/useTableDefinitionNavigationHook';
import SeasonalValueAndPercentTables from '../../Components/Tables/Seasonal/SeasonalValueAndPercentTables';
import useMarketByCommodity from '../../Core/Hooks/useMarketByCommodity';
import useUnitOfMeasure from '../../Core/Hooks/useUnitOfMeasureHook';
import { isRegionCommodityGroupedMarket } from '../../Core/MarketDefinitions';
import useLanguage from '../../Services/Language/useLanguageHook';
import styles from './StocksPage.module.scss';

const StocksPage: React.FC = () => {
    // Constants.
    const [translations] = useLanguage();
    const pageTitle = translations.stocks.title;
    const tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyStockTable;

    // Hooks.
    const [, tableDefinitions, regionCommoditySelection] = useTableDefinitionNavigation(tableDefinitionType);
    const [searchParams] = useSearchParams();
    const commentary = useCommentaryApi(tableDefinitions?.demeterCommentaryGuid);
    const [unitOfMeasure, setUnitOfMeasure] = useUnitOfMeasure('MetricImperial');
    const marketByCommodity = useMarketByCommodity(tableDefinitionType);
    const [aggregateRegions, setAggregateRegions] = useState<DemeterRegion[]>([]);

    const sugarTypeLabels: Record<string, string> = {
        Sugar: translations.commodity.SugarTelQuel,
        SugarWhite: translations.commodity.SugarWhite,
        SugarRaw: translations.commodity.SugarRaw,
    };
    const sugarCommodity = tableDefinitions?.commodity ?? searchParams.get('commodity') ?? regionCommoditySelection?.commodity ?? '';
    const sugarNote = sugarTypeLabels[sugarCommodity] || '';

    const isYearlyOnly = tableDefinitions?.dataFrequencies?.length === 1 && tableDefinitions.dataFrequencies[0] === DemeterDataFrequency.Yearly;
    const showAllChartsAndTable = isRegionCommodityGroupedMarket(marketByCommodity);

    useEffect(() => {
        setAggregateRegions([]);
    }, [regionCommoditySelection?.region]);

    return (
        <div className={styles.master_page_container}>
            <PageHeaderWithSelections
                title={pageTitle}
                tableDefinitionType={tableDefinitionType}
                rightSideOfHeaderComponent={
                    <>
                        <UnitOfMeasureMetricImperialDropdown
                            unitOfMeasure={unitOfMeasure}
                            handleChange={setUnitOfMeasure}
                            track={{ eventCategory: 'Stocks', eventAction: 'UnitOfMeasureChanged', eventValue: unitOfMeasure }}
                        />
                        <TutorialLink link={applicationConstants.TutorialLinks.fundamentalsSupplyDemand} />
                    </>
                }
                testId="StocksPageHeader"
                track={{ eventCategory: 'Stocks', eventAction: 'PageHeaderDisplayed' }}
                rightSideOfBreadcrumbsComponent={sugarNote ? <span>{sugarNote}</span> : undefined}
            />
            {showAllChartsAndTable && regionCommoditySelection && (
                <div className={styles.stocks_main_container}>
                    <div className={styles.stocks_projection_chart}>
                        <ProjectionChart
                            title={pageTitle}
                            tableDefinitionType={tableDefinitionType}
                            regionCommoditySelection={regionCommoditySelection!}
                            aggregateRegions={aggregateRegions}
                            unitOfMeasure={unitOfMeasure}
                            pageType={DemeterPageType.Stocks}
                            testId="StocksProjectionChart"
                            track={{
                                eventCategory: 'Stocks',
                                eventAction: 'ProjectionChart',
                                eventValue: { tableDefinitionType, regionCommoditySelection },
                            }}
                        />
                    </div>
                    {regionCommoditySelection?.region === DemeterRegion.Aggregate ? (
                        <div className={styles.stocks_commentary_container}>
                            <AggregateRegionSelector
                                title={translations.region.Aggregated}
                                tableDefinitionType={tableDefinitionType}
                                regionCommoditySelection={regionCommoditySelection!}
                                handleRegionSelections={setAggregateRegions}
                                track={{
                                    eventCategory: 'Stocks',
                                    eventAction: 'AggregatedRegionSelector',
                                }}
                            />
                        </div>
                    ) : (
                        tableDefinitions?.demeterCommentaryGuid && (
                            <div className={styles.stocks_commentary_container}>
                                <p>{commentary}</p>
                            </div>
                        )
                    )}
                </div>
            )}
            {!isYearlyOnly && regionCommoditySelection && (
                <div className={styles.stocks_main_container}>
                    <div className={styles.stocks_seasonal_chart}>
                        <SeasonalChart
                            title={pageTitle}
                            tableDefinitionType={tableDefinitionType}
                            regionCommoditySelection={regionCommoditySelection!}
                            aggregateRegions={aggregateRegions}
                            unitOfMeasure={unitOfMeasure}
                            pageType={DemeterPageType.Stocks}
                            testId="StocksSeasonalChart"
                            track={{
                                eventCategory: 'Stocks',
                                eventAction: 'SeasonalChart',
                                eventValue: { tableDefinitionType, regionCommoditySelection },
                            }}
                        />
                    </div>
                </div>
            )}
            {showAllChartsAndTable && !isYearlyOnly && (
                <SeasonalValueAndPercentTables
                    title={pageTitle}
                    tableDefinitionType={tableDefinitionType}
                    regionCommoditySelection={regionCommoditySelection!}
                    aggregateRegions={aggregateRegions}
                    unitOfMeasure={unitOfMeasure}
                    testId="StocksSeasonalValueAndPercentTables"
                    track={{
                        eventCategory: 'Stocks',
                        eventAction: 'SeasonalValueAndPercentTables',
                        eventValue: { tableDefinitionType, regionCommoditySelection },
                    }}
                />
            )}
        </div>
    );
};
export default StocksPage;
